import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import BulkFillerContentForm from '../components/BulkFillerContentForm';
import WordPressAutoSyncManager from '../components/WordPressAutoSyncManager';
import AdminAccountForm from '../components/AdminAccountForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token, user, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [exportData, setExportData] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [backupFile, setBackupFile] = useState('');
  
  // Ad & Revenue Management State
  const [soldAds, setSoldAds] = useState([]);
  const [affiliateAds, setAffiliateAds] = useState([]);
  const [revenueStats, setRevenueStats] = useState(null);
  const [loadingAds, setLoadingAds] = useState(false);
  
  // Sold Ad Form State
  const [soldAdForm, setSoldAdForm] = useState({
    position: 'leaderboard',
    client_name: '',
    title: '',
    image_url: '',
    click_url: '',
    size: '728x90',
    mobile_size: '',
    tablet_size: '',
    device_targeting: 'all',
    monthly_rate: '',
    expires_at: ''
  });
  const [editingSoldAd, setEditingSoldAd] = useState(null);
  
  // Affiliate Ad Form State  
  const [affiliateAdForm, setAffiliateAdForm] = useState({
    title: '',
    description: '',
    affiliate_url: '',
    image_url: '',
    size: '300x250',
    mobile_size: '',
    tablet_size: '',
    device_targeting: 'all',
    placement_type: 'in_feed',
    priority: 1
  });
  const [editingAffiliateAd, setEditingAffiliateAd] = useState(null);

  // Ad Size Options with mobile support
  const adSizeOptions = {
    leaderboard: ['728x90'],
    sidebar: ['300x250', '300x600', '160x600'], 
    before_interhell: ['728x90', '336x280']
  };

  const mobileAdSizes = ['320x50', '320x100', '300x250'];
  const tabletAdSizes = ['320x100', '728x90', '300x250', '336x280'];
  const affiliateAdSizes = ['300x250', '336x280', '320x50', '160x600'];

  // Filler Content Management State
  const [fillerContent, setFillerContent] = useState([]);
  const [selectedFillerType, setSelectedFillerType] = useState('all');
  const [fillerForm, setFillerForm] = useState({
    content_type: 'philosophy_quotes',
    title: '',
    content: '',
    author: '',
    source: '',
    category: ''
  });
  const [editingFiller, setEditingFiller] = useState(null);
  const [loadingFiller, setLoadingFiller] = useState(false);

  const fillerTypes = [
    { value: 'philosophy_quotes', label: 'Philosophy Quotes' },
    { value: 'mindfulness_tips', label: 'Mindfulness Tips' },
    { value: 'consciousness_facts', label: 'Consciousness Facts' },
    { value: 'wellness_wisdom', label: 'Wellness Wisdom' },
    { value: 'eco_insights', label: 'Eco Insights' },
    { value: 'daily_motivation', label: 'Daily Motivation' },
    { value: 'science_facts', label: 'Science Facts' },
    { value: 'spiritual_wisdom', label: 'Spiritual Wisdom' },
    { value: 'personal_growth', label: 'Personal Growth' },
    { value: 'word_power', label: 'Word Power' }
  ];

  // Debug Tools Functions
  const testRitualStatus = async () => {
    try {
      console.log('🧪 Testing ritual status...');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/daily-ritual-status`, { headers });
      console.log('📊 Full Response:', response.data);
      alert(`Ritual Status:\nNeeds Ritual: ${response.data.needs_ritual}\nCompleted Today: ${response.data.completed_today}\nLast Completion: ${response.data.last_completion || 'None'}`);
    } catch (error) {
      console.error('Error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const forceResetRitual = async () => {
    try {
      console.log('🔄 Force resetting ritual...');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/debug/reset-ritual`, {}, { headers });
      console.log('✅ Reset Response:', response.data);
      alert(`Ritual Reset: ${response.data.message}\nDeleted: ${response.data.deleted_count} completions`);
    } catch (error) {
      console.error('Error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const triggerRitualCheck = () => {
    console.log('🔄 Triggering ritual check...');
    localStorage.removeItem('last_ritual_date');
    window.location.href = '/daily-ritual';
  };

  const forceGoToRitual = () => {
    console.log('🌅 Force going to ritual...');
    window.location.href = '/daily-ritual';
  };

  const checkVisionBoardImages = async () => {
    try {
      console.log('🖼️ Checking vision board images...');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/auth/me`, { headers });
      console.log('📊 User data:', response.data);
      console.log('📸 Affirmation images:', response.data.affirmation_images);
      
      const imageCount = response.data.affirmation_images?.length || 0;
      alert(`Vision Board Status:\nAffirmations: ${response.data.affirmations?.length || 0}\nImages: ${imageCount}\n\nCheck console for details.`);
    } catch (error) {
      console.error('Error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Filler Content Management Functions
  const fetchFillerContent = async () => {
    setLoadingFiller(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = selectedFillerType !== 'all' ? { content_type: selectedFillerType } : {};
      const response = await axios.get(`${API}/admin/filler-content`, { headers, params });
      setFillerContent(response.data.filler_content || []);
    } catch (error) {
      console.error('Error fetching filler content:', error);
      alert('Failed to fetch filler content');
    } finally {
      setLoadingFiller(false);
    }
  };

  const handleCreateFillerContent = async () => {
    if (!fillerForm.title || !fillerForm.content) {
      alert('Please fill in title and content');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/filler-content`, fillerForm, { headers });
      alert('Filler content created successfully!');
      setFillerForm({
        content_type: 'philosophy_quotes',
        title: '',
        content: '',
        author: '',
        source: '',
        category: ''
      });
      fetchFillerContent();
    } catch (error) {
      console.error('Error creating filler content:', error);
      alert(`Failed to create filler content: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUpdateFillerContent = async () => {
    if (!editingFiller || !fillerForm.title || !fillerForm.content) {
      alert('Please fill in title and content');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/filler-content/${editingFiller.id}`, fillerForm, { headers });
      alert('Filler content updated successfully!');
      setEditingFiller(null);
      setFillerForm({
        content_type: 'philosophy_quotes',
        title: '',
        content: '',
        author: '',
        source: '',
        category: ''
      });
      fetchFillerContent();
    } catch (error) {
      console.error('Error updating filler content:', error);
      alert(`Failed to update filler content: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteFillerContent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this filler content?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/filler-content/${id}`, { headers });
      alert('Filler content deleted successfully!');
      fetchFillerContent();
    } catch (error) {
      console.error('Error deleting filler content:', error);
      alert(`Failed to delete filler content: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEditFillerContent = (item) => {
    setEditingFiller(item);
    setFillerForm({
      content_type: item.content_type,
      title: item.title,
      content: item.content,
      author: item.author || '',
      source: item.source || '',
      category: item.category || ''
    });
  };

  const cancelFillerEdit = () => {
    setEditingFiller(null);
    setFillerForm({
      content_type: 'philosophy_quotes',
      title: '',
      content: '',
      author: '',
      source: '',
      category: ''
    });
  };

  useEffect(() => {
    if (activeTab === 'filler') {
      fetchFillerContent();
    }
  }, [activeTab, selectedFillerType]);

  useEffect(() => {
    if (activeTab === 'ads') {
      fetchSoldAds();
      fetchAffiliateAds();
      fetchRevenueStats();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchDatabaseStats();
    }
  }, [user, isSuperAdmin]);

  const fetchDatabaseStats = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/database/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching database stats:', error);
      alert('Failed to fetch database statistics');
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/database/export`, {
        include_sensitive_data: false,
        anonymize_users: false
      }, { headers });
      
      console.log('✅ Export response received:', response.data);
      setExportData(response.data);
      alert('Data exported successfully! You can now download the backup package.');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert(`Failed to export data: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImportData = async () => {
    if (!importFile) {
      alert('Please select a file to import');
      return;
    }

    setLoading(true);
    setImportProgress('Reading file...');
    
    try {
      // Read the file
      const fileText = await importFile.text();
      const importData = JSON.parse(fileText);
      
      setImportProgress('Validating data...');
      
      // Validate the data structure
      if (!importData || typeof importData !== 'object') {
        throw new Error('Invalid file format: Expected JSON object');
      }

      setImportProgress('Importing to database...');
      
      // Send to backend
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const importRequest = {
        merge_strategy: 'replace',
        backup_existing: true
      };

      const response = await axios.post(
        `${API}/admin/database/import`, 
        {
          import_request: importRequest,
          import_data: importData
        },
        { headers }
      );
      
      setImportResult(response.data);
      setImportProgress(null);
      alert('Data imported successfully! Your data should now be visible in the dashboard.');
      
      // Refresh stats
      fetchDatabaseStats();
      
    } catch (error) {
      console.error('Error importing data:', error);
      setImportProgress(null);
      let errorMessage = 'Failed to import data';
      
      if (error.response?.data?.detail) {
        errorMessage += `: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    setImportFile(file);
    setImportResult(null);
    setImportProgress(null);
  };

  const handleCreateBackup = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/database/backup`, {}, { headers });
      
      setBackupFile(response.data.backup_file);
      alert(`Backup created successfully: ${response.data.backup_file}`);
    } catch (error) {
      console.error('Error creating backup:', error);
      alert(`Failed to create backup: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = (filename) => {
    const downloadUrl = `${API}/admin/database/backup/${filename}`;
    const headers = { Authorization: `Bearer ${token}` };
    
    // Create a temporary link to download the file
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    
    // Add authorization header by creating a fetch request
    fetch(downloadUrl, { headers })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Error downloading backup:', error);
        alert('Failed to download backup file');
      });
  };

  const handleRestoreBackup = async () => {
    if (!importFile) {
      alert('Please select a backup file to restore');
      return;
    }

    if (!window.confirm('⚠️ This will REPLACE all existing data with the backup data. Are you sure?')) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const headers = { 
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type, let the browser set it with boundary
      };

      const response = await axios.post(`${API}/admin/database/restore`, formData, { headers });
      
      alert('Platform restored successfully from backup!');
      await fetchDatabaseStats(); // Refresh stats
      setImportFile(null);
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert(`Failed to restore backup: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Ad & Revenue Management Functions
  const fetchSoldAds = async () => {
    setLoadingAds(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/sold-ads`, { headers });
      setSoldAds(response.data.sold_ads || []);
    } catch (error) {
      console.error('Error fetching sold ads:', error);
    } finally {
      setLoadingAds(false);
    }
  };

  const fetchAffiliateAds = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/affiliate-ads`, { headers });
      setAffiliateAds(response.data.affiliate_ads || []);
    } catch (error) {
      console.error('Error fetching affiliate ads:', error);
    }
  };

  const fetchRevenueStats = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/ad-revenue/stats`, { headers });
      setRevenueStats(response.data);
    } catch (error) {
      console.error('Error fetching revenue stats:', error);
    }
  };

  const handleSoldAdSubmit = async (e) => {
    e.preventDefault();
    setLoadingAds(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = {
        ...soldAdForm,
        monthly_rate: parseFloat(soldAdForm.monthly_rate),
        expires_at: soldAdForm.expires_at ? new Date(soldAdForm.expires_at).toISOString() : null
      };
      
      if (editingSoldAd) {
        await axios.put(`${API}/sold-ads/${editingSoldAd.id}`, formData, { headers });
        alert('✅ Sold ad updated successfully!');
        setEditingSoldAd(null);
      } else {
        await axios.post(`${API}/sold-ads`, formData, { headers });
        alert('✅ Sold ad created successfully!');
      }
      
      // Reset form
      setSoldAdForm({
        position: 'leaderboard',
        client_name: '',
        title: '',
        image_url: '',
        click_url: '',
        size: '728x90',
        monthly_rate: '',
        expires_at: ''
      });
      
      await fetchSoldAds();
      await fetchRevenueStats();
      
    } catch (error) {
      console.error('Error saving sold ad:', error);
      alert(`Failed to save sold ad: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoadingAds(false);
    }
  };

  const handleAffiliateAdSubmit = async (e) => {
    e.preventDefault();
    setLoadingAds(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = {
        ...affiliateAdForm,
        priority: parseInt(affiliateAdForm.priority)
      };
      
      if (editingAffiliateAd) {
        await axios.put(`${API}/affiliate-ads/${editingAffiliateAd.id}`, formData, { headers });
        alert('✅ Affiliate ad updated successfully!');
        setEditingAffiliateAd(null);
      } else {
        await axios.post(`${API}/affiliate-ads`, formData, { headers });
        alert('✅ Affiliate ad created successfully!');
      }
      
      // Reset form
      setAffiliateAdForm({
        title: '',
        description: '',
        affiliate_url: '',
        image_url: '',
        size: '300x250',
        placement_type: 'in_feed',
        priority: 1
      });
      
      await fetchAffiliateAds();
      await fetchRevenueStats();
      
    } catch (error) {
      console.error('Error saving affiliate ad:', error);
      alert(`Failed to save affiliate ad: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoadingAds(false);
    }
  };

  const deleteSoldAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this sold ad?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/sold-ads/${adId}`, { headers });
      alert('✅ Sold ad deleted successfully!');
      await fetchSoldAds();
      await fetchRevenueStats();
    } catch (error) {
      console.error('Error deleting sold ad:', error);
      alert(`Failed to delete sold ad: ${error.response?.data?.detail || error.message}`);
    }
  };

  const deleteAffiliateAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this affiliate ad?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/affiliate-ads/${adId}`, { headers });
      alert('✅ Affiliate ad deleted successfully!');
      await fetchAffiliateAds();
      await fetchRevenueStats();
    } catch (error) {
      console.error('Error deleting affiliate ad:', error);
      alert(`Failed to delete affiliate ad: ${error.response?.data?.detail || error.message}`);
    }
  };

  const editSoldAd = (ad) => {
    setSoldAdForm({
      position: ad.position,
      client_name: ad.client_name,
      title: ad.title,
      image_url: ad.image_url || '',
      click_url: ad.click_url,
      size: ad.size,
      mobile_size: ad.mobile_size || '',
      tablet_size: ad.tablet_size || '',
      device_targeting: ad.device_targeting || 'all',
      monthly_rate: ad.monthly_rate.toString(),
      expires_at: ad.expires_at ? new Date(ad.expires_at).toISOString().split('T')[0] : ''
    });
    setEditingSoldAd(ad);
  };

  const editAffiliateAd = (ad) => {
    setAffiliateAdForm({
      title: ad.title,
      description: ad.description || '',
      affiliate_url: ad.affiliate_url,
      image_url: ad.image_url || '',
      size: ad.size,
      mobile_size: ad.mobile_size || '',
      tablet_size: ad.tablet_size || '',
      device_targeting: ad.device_targeting || 'all',
      placement_type: ad.placement_type,
      priority: ad.priority.toString()
    });
    setEditingAffiliateAd(ad);
  };

  // Check if user is the super-admin
  if (!user || !isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600">Admin privileges required to access this page.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'account', label: 'Admin Account', icon: '👨‍💼' },
    { id: 'wordpress', label: 'WordPress Auto-Sync', icon: '📰' },
    { id: 'ads', label: 'Ad & Revenue Manager', icon: '💰' },
    { id: 'conscious-commerce', label: 'Conscious Commerce', icon: '🛍️' },
    { id: 'filler', label: 'Filler Posts', icon: '✨' },
    { id: 'backup', label: 'Backup & Restore', icon: '💾' },
    { id: 'import', label: 'Data Import', icon: '📥' },
    { id: 'export', label: 'Data Export', icon: '📤' },
    { id: 'stats', label: 'Database Stats', icon: '📈' },
    { id: 'debug', label: 'Debug Tools', icon: '🧪' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">⚙️</div>
              <div>
                <h1 className="text-3xl font-bold">Admin Dashboard</h1>
                <p className="text-blue-100">Platform Management & Data Operations</p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Platform Overview</h2>
              
              {stats && (
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl p-6">
                    <div className="text-3xl mb-2">👥</div>
                    <div className="text-2xl font-bold">{stats.collections.users?.document_count || 0}</div>
                    <div className="text-emerald-100">Total Users</div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl p-6">
                    <div className="text-3xl mb-2">📝</div>
                    <div className="text-2xl font-bold">{stats.collections.posts?.document_count || 0}</div>
                    <div className="text-blue-100">Total Posts</div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl p-6">
                    <div className="text-3xl mb-2">🌟</div>
                    <div className="text-2xl font-bold">{stats.collections.reality_garden?.document_count || 0}</div>
                    <div className="text-purple-100">Reality Garden Entries</div>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <button
                    onClick={handleCreateBackup}
                    disabled={loading}
                    className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <span>💾</span>
                    <span>Create Backup</span>
                  </button>
                  
                  <button
                    onClick={fetchDatabaseStats}
                    disabled={loading}
                    className="flex items-center justify-center space-x-2 bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    <span>🔄</span>
                    <span>Refresh Stats</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* User Management Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="text-3xl">👥</div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">User Management System</h2>
                  <p className="text-gray-600">Manage user accounts, permissions, and platform access</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl">
                    🚀
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">Advanced User Management Available</h3>
                    <p className="text-gray-600">Access the full user management system with advanced features</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-gray-800 mb-2">✨ Available Features:</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• User list with search and filtering</li>
                      <li>• Individual user account management</li>
                      <li>• Bulk user actions (ban, suspend, etc.)</li>
                      <li>• Password reset management</li>
                      <li>• User data export (GDPR compliance)</li>
                      <li>• Comprehensive audit logging</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-gray-800 mb-2">🛡️ Security Features:</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Secure password management</li>
                      <li>• Account status controls</li>
                      <li>• Admin action tracking</li>
                      <li>• Confirmation dialogs for safety</li>
                      <li>• Role-based access control</li>
                      <li>• Data export with privacy controls</li>
                    </ul>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="/admin/users"
                    className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                  >
                    <span className="mr-2">👥</span>
                    Open User Management System
                  </a>
                  
                  <div className="flex items-center text-gray-600 text-sm">
                    <span className="mr-2">ℹ️</span>
                    <span>Full-featured user administration dashboard</span>
                  </div>
                </div>
              </div>

              {/* Quick Stats Preview */}
              {stats && (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Quick User Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{stats.collections.users?.document_count || 0}</div>
                      <div className="text-xs text-gray-500">Total Users</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">-</div>
                      <div className="text-xs text-gray-500">Active Users</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">-</div>
                      <div className="text-xs text-gray-500">Suspended</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">-</div>
                      <div className="text-xs text-gray-500">Banned</div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    Open the User Management System for detailed statistics and management tools
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Ad & Revenue Management Tab */}
          {activeTab === 'account' && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">👨‍💼 Admin Account Management</h2>
              
              <AdminAccountForm />
            </div>
          )}

          {activeTab === 'wordpress' && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <WordPressAutoSyncManager />
            </div>
          )}

          {activeTab === 'ads' && (
            <div className="space-y-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="text-3xl">💰</div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Ad & Revenue Manager</h2>
                  <p className="text-gray-600">Manage sold ads, affiliate marketing, and track revenue</p>
                </div>
              </div>

              {/* Revenue Stats Overview */}
              {revenueStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-800">
                      ${revenueStats.total_revenue_month.toFixed(2)}
                    </div>
                    <div className="text-sm text-green-600">Total Monthly Revenue</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-800">
                      {revenueStats.active_sold_ads}
                    </div>
                    <div className="text-sm text-blue-600">Active Sold Ads</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-800">
                      {revenueStats.active_affiliate_ads}
                    </div>
                    <div className="text-sm text-purple-600">Active Affiliate Ads</div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-800">
                      ${revenueStats.post_boost_revenue_month.toFixed(2)}
                    </div>
                    <div className="text-sm text-yellow-600">Post Boost Revenue</div>
                  </div>
                </div>
              )}

              {/* Sold Ads Management */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-4">🌟 Sold Ads (Premium Placement)</h3>
                
                <form onSubmit={handleSoldAdSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-2">Position</label>
                      <select
                        value={soldAdForm.position}
                        onChange={(e) => {
                          const position = e.target.value;
                          setSoldAdForm({
                            ...soldAdForm, 
                            position,
                            size: adSizeOptions[position][0] // Set default size for position
                          });
                        }}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      >
                        <option value="leaderboard">Leaderboard (Top of Feed)</option>
                        <option value="sidebar">Sidebar (Premium)</option>
                        <option value="before_interhell">Before Interhell</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-2">Size</label>
                      <select
                        value={soldAdForm.size}
                        onChange={(e) => setSoldAdForm({...soldAdForm, size: e.target.value})}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      >
                        {adSizeOptions[soldAdForm.position]?.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-2">Client Name</label>
                      <input
                        type="text"
                        value={soldAdForm.client_name}
                        onChange={(e) => setSoldAdForm({...soldAdForm, client_name: e.target.value})}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Company or Client Name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-2">Monthly Rate ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={soldAdForm.monthly_rate}
                        onChange={(e) => setSoldAdForm({...soldAdForm, monthly_rate: e.target.value})}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="500.00"
                        required
                      />
                    </div>
                  </div>

                  {/* Mobile & Tablet Sizing */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3">📱 Responsive Ad Sizes</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Mobile Size</label>
                        <select
                          value={soldAdForm.mobile_size}
                          onChange={(e) => setSoldAdForm({...soldAdForm, mobile_size: e.target.value})}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Auto (Responsive)</option>
                          {mobileAdSizes.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Tablet Size</label>
                        <select
                          value={soldAdForm.tablet_size}
                          onChange={(e) => setSoldAdForm({...soldAdForm, tablet_size: e.target.value})}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Auto (Responsive)</option>
                          {tabletAdSizes.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Device Target</label>
                        <select
                          value={soldAdForm.device_targeting}
                          onChange={(e) => setSoldAdForm({...soldAdForm, device_targeting: e.target.value})}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="all">All Devices</option>
                          <option value="desktop">Desktop Only</option>
                          <option value="mobile">Mobile Only</option>
                          <option value="tablet">Tablet Only</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      💡 Mobile ads ensure readability on small screens. Auto-responsive uses optimized sizes based on device.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">Ad Title</label>
                    <input
                      type="text"
                      value={soldAdForm.title}
                      onChange={(e) => setSoldAdForm({...soldAdForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Ad headline or title"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-2">Image URL</label>
                      <input
                        type="url"
                        value={soldAdForm.image_url}
                        onChange={(e) => setSoldAdForm({...soldAdForm, image_url: e.target.value})}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="https://example.com/ad-image.jpg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-2">Click URL</label>
                      <input
                        type="url"
                        value={soldAdForm.click_url}
                        onChange={(e) => setSoldAdForm({...soldAdForm, click_url: e.target.value})}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="https://client-website.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-2">Expires At (Optional)</label>
                    <input
                      type="date"
                      value={soldAdForm.expires_at}
                      onChange={(e) => setSoldAdForm({...soldAdForm, expires_at: e.target.value})}
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      disabled={loadingAds}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {loadingAds ? 'Saving...' : (editingSoldAd ? 'Update Sold Ad' : 'Create Sold Ad')}
                    </button>
                    
                    {editingSoldAd && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingSoldAd(null);
                          setSoldAdForm({
                            position: 'leaderboard',
                            client_name: '',
                            title: '',
                            image_url: '',
                            click_url: '',
                            size: '728x90',
                            mobile_size: '',
                            tablet_size: '',
                            device_targeting: 'all',
                            monthly_rate: '',
                            expires_at: ''
                          });
                        }}
                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </form>

                {/* Sold Ads List */}
                {soldAds.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-green-800 mb-3">Current Sold Ads</h4>
                    <div className="space-y-2">
                      {soldAds.map(ad => (
                        <div key={ad.id} className="flex items-center justify-between bg-white p-3 rounded border">
                          <div className="flex-1">
                            <div className="font-medium">{ad.title}</div>
                            <div className="text-sm text-gray-600">
                              {ad.client_name} • {ad.position} • Desktop: {ad.size}
                              {ad.mobile_size && ` • Mobile: ${ad.mobile_size}`}
                              {ad.tablet_size && ` • Tablet: ${ad.tablet_size}`}
                              • ${ad.monthly_rate}/month • Target: {ad.device_targeting || 'all'}
                              {ad.expires_at && ` • Expires: ${new Date(ad.expires_at).toLocaleDateString()}`}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Total: {ad.total_clicks} clicks • Mobile: {ad.mobile_clicks || 0} • Desktop: {ad.desktop_clicks || 0}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => editSoldAd(ad)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteSoldAd(ad.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Affiliate Ads Management */}
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-6 border-2 border-purple-200">
                <h3 className="text-lg font-semibold text-purple-800 mb-4">🔗 Affiliate Ads (Random Placement)</h3>
                
                <form onSubmit={handleAffiliateAdSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-2">Ad Title</label>
                      <input
                        type="text"
                        value={affiliateAdForm.title}
                        onChange={(e) => setAffiliateAdForm({...affiliateAdForm, title: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Product or Service Name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-2">Size</label>
                      <select
                        value={affiliateAdForm.size}
                        onChange={(e) => setAffiliateAdForm({...affiliateAdForm, size: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      >
                        {affiliateAdSizes.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Mobile & Tablet Sizing for Affiliate Ads */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3">📱 Responsive Affiliate Ad Sizes</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Mobile Size</label>
                        <select
                          value={affiliateAdForm.mobile_size}
                          onChange={(e) => setAffiliateAdForm({...affiliateAdForm, mobile_size: e.target.value})}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Auto (Responsive)</option>
                          {mobileAdSizes.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Tablet Size</label>
                        <select
                          value={affiliateAdForm.tablet_size}
                          onChange={(e) => setAffiliateAdForm({...affiliateAdForm, tablet_size: e.target.value})}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Auto (Responsive)</option>
                          {tabletAdSizes.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Device Target</label>
                        <select
                          value={affiliateAdForm.device_targeting}
                          onChange={(e) => setAffiliateAdForm({...affiliateAdForm, device_targeting: e.target.value})}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="all">All Devices</option>
                          <option value="desktop">Desktop Only</option>
                          <option value="mobile">Mobile Only</option>
                          <option value="tablet">Tablet Only</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      💡 Mobile affiliate ads perform better with optimized sizes. Auto mode uses best size for each device.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-2">Description</label>
                    <textarea
                      value={affiliateAdForm.description}
                      onChange={(e) => setAffiliateAdForm({...affiliateAdForm, description: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Brief description of the product/service"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-2">Affiliate URL</label>
                      <input
                        type="url"
                        value={affiliateAdForm.affiliate_url}
                        onChange={(e) => setAffiliateAdForm({...affiliateAdForm, affiliate_url: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="https://affiliate-link.com"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-2">Image URL</label>
                      <input
                        type="url"
                        value={affiliateAdForm.image_url}
                        onChange={(e) => setAffiliateAdForm({...affiliateAdForm, image_url: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="https://example.com/product-image.jpg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-2">Placement Type</label>
                      <select
                        value={affiliateAdForm.placement_type}
                        onChange={(e) => setAffiliateAdForm({...affiliateAdForm, placement_type: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="in_feed">In Feed</option>
                        <option value="sidebar">Sidebar</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-2">Priority (1-5)</label>
                      <select
                        value={affiliateAdForm.priority}
                        onChange={(e) => setAffiliateAdForm({...affiliateAdForm, priority: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="1">1 - Lowest</option>
                        <option value="2">2 - Low</option>
                        <option value="3">3 - Medium</option>
                        <option value="4">4 - High</option>
                        <option value="5">5 - Highest</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      disabled={loadingAds}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {loadingAds ? 'Saving...' : (editingAffiliateAd ? 'Update Affiliate Ad' : 'Create Affiliate Ad')}
                    </button>
                    
                    {editingAffiliateAd && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingAffiliateAd(null);
                          setAffiliateAdForm({
                            title: '',
                            description: '',
                            affiliate_url: '',
                            image_url: '',
                            size: '300x250',
                            mobile_size: '',
                            tablet_size: '',
                            device_targeting: 'all',
                            placement_type: 'in_feed',
                            priority: 1
                          });
                        }}
                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </form>

                {/* Affiliate Ads List */}
                {affiliateAds.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-purple-800 mb-3">Current Affiliate Ads</h4>
                    <div className="space-y-2">
                      {affiliateAds.map(ad => (
                        <div key={ad.id} className="flex items-center justify-between bg-white p-3 rounded border">
                          <div className="flex-1">
                            <div className="font-medium">{ad.title}</div>
                            <div className="text-sm text-gray-600">
                              {ad.size} • {ad.placement_type} • Priority: {ad.priority}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Clicks: {ad.total_clicks} • Impressions: {ad.total_impressions}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => editAffiliateAd(ad)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteAffiliateAd(ad.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Ad Performance Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">💡 Ad Management Tips</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>🎯 <span className="font-medium">Sold Ads:</span> Premium placement with guaranteed visibility</li>
                  <li>🔄 <span className="font-medium">Affiliate Ads:</span> Random placement, higher priority = more frequent display</li>
                  <li>📊 <span className="font-medium">Performance:</span> Track clicks and impressions to optimize placement</li>
                  <li>💰 <span className="font-medium">Revenue:</span> Sold ads provide predictable monthly income</li>
                </ul>
              </div>
            </div>
          )}

          {/* Filler Posts Tab */}
          {activeTab === 'conscious-commerce' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">🛍️ Conscious Commerce Management</h2>
                
                {/* Import from Printify Section */}
                <div className="mb-8 p-4 border border-purple-200 rounded-lg bg-purple-50">
                  <h3 className="text-lg font-semibold text-purple-800 mb-4">📦 Import from Printify</h3>
                  <div className="space-y-4">
                    <button
                      onClick={() => importFromPrintify()}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Import Printify Products
                    </button>
                    <p className="text-sm text-purple-700">
                      This will import your Printify products. You can then edit the store links to point to the correct sales pages.
                    </p>
                  </div>
                </div>

                {/* Manual Product Entry Section */}
                <div className="mb-8 p-4 border border-green-200 rounded-lg bg-green-50">
                  <h3 className="text-lg font-semibold text-green-800 mb-4">➕ Add Manual Product</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Product Title"
                      className="border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <input
                      type="text"
                      placeholder="Store Link (Correct URL)"
                      className="border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <input
                      type="text"
                      placeholder="Image URL"
                      className="border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <input
                      type="number"
                      placeholder="Price"
                      className="border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <select className="border border-gray-300 rounded-lg px-3 py-2">
                      <option value="filler">Filler Placement</option>
                      <option value="sidebar">Sidebar Placement</option>
                    </select>
                    <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                      Add Product
                    </button>
                  </div>
                </div>

                {/* Current Products List */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Current Products (Edit Links)</h3>
                  <div className="space-y-3">
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="text-sm text-gray-600">Products will appear here after import/creation</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'filler' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">✨ Filler Content Management</h2>
              
              {/* Bulk Import Form */}
              <BulkFillerContentForm />
              
              {/* Filter and Create */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Type</label>
                    <select
                      value={selectedFillerType}
                      onChange={(e) => setSelectedFillerType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="all">All Types</option>
                      {fillerTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={fetchFillerContent}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      🔄 Refresh
                    </button>
                  </div>
                </div>

                {/* Create/Edit Form */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {editingFiller ? '✏️ Edit Filler Content' : '➕ Create New Filler Content'}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
                      <select
                        value={fillerForm.content_type}
                        onChange={(e) => setFillerForm({...fillerForm, content_type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {fillerTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                      <input
                        type="text"
                        value={fillerForm.title}
                        onChange={(e) => setFillerForm({...fillerForm, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Enter title..."
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                    <textarea
                      value={fillerForm.content}
                      onChange={(e) => setFillerForm({...fillerForm, content: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Enter content..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Author (Optional)</label>
                      <input
                        type="text"
                        value={fillerForm.author}
                        onChange={(e) => setFillerForm({...fillerForm, author: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Enter author..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Source (Optional)</label>
                      <input
                        type="text"
                        value={fillerForm.source}
                        onChange={(e) => setFillerForm({...fillerForm, source: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Enter source..."
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {editingFiller ? (
                      <>
                        <button
                          onClick={handleUpdateFillerContent}
                          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          ✅ Update Content
                        </button>
                        <button
                          onClick={cancelFillerEdit}
                          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          ❌ Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleCreateFillerContent}
                        className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        ➕ Create Content
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Content List */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  📝 Existing Filler Content ({fillerContent.length} items)
                </h3>
                
                {loadingFiller ? (
                  <div className="text-center py-8">
                    <div className="animate-spin text-2xl">⏳</div>
                    <p className="mt-2 text-gray-600">Loading filler content...</p>
                  </div>
                ) : fillerContent.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No filler content found for the selected type.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {fillerContent.map((item) => (
                      <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">{item.title}</h4>
                            <p className="text-sm text-gray-600 mb-2">{item.content}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded">
                                {fillerTypes.find(t => t.value === item.content_type)?.label || item.content_type}
                              </span>
                              {item.author && <span>👤 {item.author}</span>}
                              {item.source && <span>📚 {item.source}</span>}
                              <span>👁️ {item.display_count || 0} views</span>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleEditFillerContent(item)}
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors text-sm"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteFillerContent(item.id)}
                              className="px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors text-sm"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">📋 How Filler Content Works</h3>
                <ul className="space-y-2 text-sm text-blue-700">
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500">•</span>
                    <span>Filler content is automatically interspersed between user posts in the main feed</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500">•</span>
                    <span>Different content types help maintain variety and engagement</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500">•</span>
                    <span>You can continuously add new content to keep the feed fresh</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500">•</span>
                    <span>All content is automatically displayed with proper styling and formatting</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Backup & Restore Tab */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Backup & Restore</h2>
              
              {/* Create Backup */}
              <div className="border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">📦 Create Backup Package</h3>
                <p className="text-gray-600 mb-4">
                  Create a complete backup package containing all platform data, metadata, and restore scripts.
                </p>
                <button
                  onClick={handleCreateBackup}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Creating Backup...' : 'Create Full Backup'}
                </button>
                
                {backupFile && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-800 font-medium">✅ Backup Created Successfully</p>
                        <p className="text-green-600 text-sm">{backupFile}</p>
                      </div>
                      <button
                        onClick={() => handleDownloadBackup(backupFile)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Restore from Backup */}
              <div className="border border-red-200 rounded-xl p-6 bg-red-50">
                <h3 className="text-lg font-semibold text-red-800 mb-4">🔄 Restore from Backup</h3>
                <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4">
                  <p className="text-red-800 font-medium">⚠️ Warning</p>
                  <p className="text-red-700 text-sm">This will replace ALL existing platform data with the backup data. This action cannot be undone.</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Backup File (.zip)
                    </label>
                    <input
                      type="file"
                      accept=".zip"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  
                  <button
                    onClick={handleRestoreBackup}
                    disabled={loading || !importFile}
                    className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Restoring...' : 'Restore Platform'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Data Import Tab */}
          {activeTab === 'import' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Data Import</h2>
              
              <div className="border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">📥 Import Platform Data</h3>
                <p className="text-gray-600 mb-4">
                  Import platform data from JSON format. This will merge your development data with production data.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Import File (JSON)
                    </label>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {importFile && (
                      <p className="mt-2 text-sm text-gray-600">
                        📁 Selected: {importFile.name} ({Math.round(importFile.size / 1024)} KB)
                      </p>
                    )}
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="text-yellow-600 mr-3">⚠️</div>
                      <div>
                        <h4 className="text-yellow-800 font-medium">Import Strategy</h4>
                        <p className="text-yellow-700 text-sm mt-1">
                          This will replace existing data with imported data. A backup will be created automatically before import.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleImportData}
                    disabled={loading || !importFile}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? (importProgress || 'Importing...') : 'Import Data'}
                  </button>
                </div>

                {importResult && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="text-green-800 font-medium mb-2">✅ Import Completed</h4>
                    <div className="space-y-2 text-sm text-green-700">
                      {importResult.import_stats && (
                        <>
                          <p>Collections Imported: {Object.keys(importResult.import_stats).length}</p>
                          <p>Total Documents: {Object.values(importResult.import_stats).reduce((a, b) => a + b, 0)}</p>
                        </>
                      )}
                      <p className="text-green-600 font-medium">Your data has been successfully imported to production!</p>
                    </div>
                  </div>
                )}

                {importProgress && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                      <p className="text-blue-800">{importProgress}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 Quick Migration</h3>
                <p className="text-gray-600 mb-4">
                  If you have the migration file from your development environment, you can use it here:
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-mono text-gray-700">
                    Expected files:
                  </p>
                  <ul className="text-sm text-gray-600 mt-2 ml-4">
                    <li>• merged_migration_data_*.json</li>
                    <li>• production_migration_data.json</li>
                    <li>• Any exported JSON data file</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Data Export Tab */}
          {activeTab === 'export' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Data Export</h2>
              
              <div className="border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">📤 Export Platform Data</h3>
                <p className="text-gray-600 mb-4">
                  Export all platform data in JSON format for development, migration, or backup purposes.
                </p>
                
                <button
                  onClick={handleExportData}
                  disabled={loading}
                  className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Exporting...' : 'Export All Data'}
                </button>

                {exportData && (
                  <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="text-emerald-800 font-medium mb-2">✅ Export Completed</h4>
                    <div className="space-y-2 text-sm text-emerald-700">
                      <p>Total Collections: {exportData.export_metadata.total_collections}</p>
                      <p>Total Documents: {exportData.export_metadata.total_documents}</p>
                      <p>Timestamp: {exportData.export_metadata.timestamp}</p>
                    </div>
                    <div className="mt-4 space-y-3">
                      <button
                        onClick={() => {
                          try {
                            // Use the data property or fallback to the full response
                            const dataToExport = exportData.data || exportData;
                            const dataStr = JSON.stringify(dataToExport, null, 2);
                            
                            // Create timestamp if not available
                            const timestamp = exportData.export_metadata?.timestamp || 
                                            new Date().toISOString().replace(/[:.]/g, '-');
                            const exportFileDefaultName = `mhb_export_${timestamp}.json`;
                            
                            // Try multiple download methods
                            try {
                              // Method 1: Modern Blob approach
                              const blob = new Blob([dataStr], { type: 'application/json' });
                              const url = URL.createObjectURL(blob);
                              
                              const linkElement = document.createElement('a');
                              linkElement.href = url;
                              linkElement.download = exportFileDefaultName;
                              linkElement.style.display = 'none';
                              
                              document.body.appendChild(linkElement);
                              linkElement.click();
                              document.body.removeChild(linkElement);
                              URL.revokeObjectURL(url);
                              
                              console.log('✅ Download method 1 successful');
                              alert('✅ Download started! Check your downloads folder.');
                              
                            } catch (blobError) {
                              console.log('Method 1 failed, trying method 2:', blobError);
                              
                              // Method 2: Data URI fallback
                              const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
                              const linkElement = document.createElement('a');
                              linkElement.href = dataUri;
                              linkElement.download = exportFileDefaultName;
                              linkElement.click();
                              
                              console.log('✅ Download method 2 successful');
                              alert('✅ Download started! Check your downloads folder.');
                            }
                            
                          } catch (error) {
                            console.error('❌ All download methods failed:', error);
                            alert(`❌ Download failed. Please use the "Show JSON Text" option below and copy manually.`);
                          }
                        }}
                        className="w-full bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        📥 Download JSON File
                      </button>
                      
                      {/* Fallback copy method */}
                      <button
                        onClick={() => {
                          try {
                            const dataToExport = exportData.data || exportData;
                            const dataStr = JSON.stringify(dataToExport, null, 2);
                            
                            // Create a text area for fallback copying
                            const textArea = document.createElement('textarea');
                            textArea.value = dataStr;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            textArea.style.top = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();
                            
                            try {
                              // Try modern clipboard API first
                              navigator.clipboard.writeText(dataStr).then(() => {
                                alert('✅ Data copied to clipboard!');
                                document.body.removeChild(textArea);
                              }).catch(() => {
                                // Fallback to older method
                                const successful = document.execCommand('copy');
                                document.body.removeChild(textArea);
                                if (successful) {
                                  alert('✅ Data copied to clipboard!');
                                } else {
                                  throw new Error('Copy command failed');
                                }
                              });
                            } catch (clipboardError) {
                              // Final fallback - show in alert/modal
                              document.body.removeChild(textArea);
                              alert('❌ Clipboard access blocked. Please use "Show JSON Text" button below and copy manually.');
                            }
                            
                          } catch (error) {
                            console.error('❌ Copy failed:', error);
                            alert('❌ Copy failed. Please use "Show JSON Text" button below.');
                          }
                        }}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        📋 Copy to Clipboard
                      </button>
                      
                      {/* Show text fallback */}
                      <button
                        onClick={() => {
                          try {
                            const dataToExport = exportData.data || exportData;
                            const dataStr = JSON.stringify(dataToExport, null, 2);
                            
                            // Create a modal or new window with the JSON
                            const newWindow = window.open('', '_blank');
                            if (newWindow) {
                              newWindow.document.write(`
                                <html>
                                <head><title>Migration Data - Copy This Text</title></head>
                                <body style="font-family: monospace; padding: 20px;">
                                  <h2>📋 Copy This JSON Data</h2>
                                  <p>Select All (Ctrl+A) and Copy (Ctrl+C):</p>
                                  <textarea style="width: 100%; height: 80vh; font-family: monospace;">${dataStr}</textarea>
                                  <p><strong>Save this as:</strong> migration_data.json</p>
                                </body>
                                </html>
                              `);
                              newWindow.document.close();
                            } else {
                              // If popup blocked, show in alert
                              alert('Please allow popups, then try again. Or check the browser console for the JSON data.');
                              console.log('=== MIGRATION DATA (Copy this) ===');
                              console.log(dataStr);
                              console.log('=== END MIGRATION DATA ===');
                            }
                          } catch (error) {
                            console.error('❌ Show text failed:', error);
                            alert('Error showing text. Check browser console for data.');
                          }
                        }}
                        className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        📄 Show JSON Text (Manual Copy)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Database Stats Tab */}
          {activeTab === 'stats' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Database Statistics</h2>
                <button
                  onClick={fetchDatabaseStats}
                  disabled={loading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  🔄 Refresh
                </button>
              </div>
              
              {stats ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Overview</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{stats.total_collections}</div>
                        <div className="text-gray-600">Collections</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-600">{stats.total_documents}</div>
                        <div className="text-gray-600">Documents</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {new Date(stats.timestamp).toLocaleDateString()}
                        </div>
                        <div className="text-gray-600">Last Updated</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-800">Collection Details</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collection</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schema Fields</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Object.entries(stats.collections).map(([name, data]) => (
                            <tr key={name}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {data.document_count}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                <div className="max-w-xs truncate">
                                  {data.schema_fields.slice(0, 5).join(', ')}
                                  {data.schema_fields.length > 5 && '...'}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📊</div>
                  <p className="text-gray-600">Loading database statistics...</p>
                </div>
              )}
            </div>
          )}

          {/* Debug Tools Tab */}
          {activeTab === 'debug' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">🧪 Debug Tools</h2>
              <p className="text-gray-600 mb-6">Administrative debugging tools for testing platform functionality.</p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Ritual Debug Tools */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-red-800 mb-4">🌅 Ritual Debugging</h3>
                  <div className="space-y-3">
                    <button 
                      onClick={testRitualStatus}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      📊 Test Ritual Status
                    </button>
                    <button 
                      onClick={forceResetRitual}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      🔄 Force Reset Ritual
                    </button>
                    <button 
                      onClick={triggerRitualCheck}
                      className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      🔄 Trigger Check & Reload
                    </button>
                    <button 
                      onClick={forceGoToRitual}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      🌅 Force Go To Ritual
                    </button>
                  </div>
                </div>

                {/* User Data Debug Tools */}
                <div className="bg-pink-50 border border-pink-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-pink-800 mb-4">👤 User Data Debugging</h3>
                  <div className="space-y-3">
                    <button 
                      onClick={checkVisionBoardImages}
                      className="w-full bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      🖼️ Check Vision Board
                    </button>
                    <button 
                      onClick={() => {
                        console.log('Current user:', user);
                        console.log('Auth token exists:', !!token);
                        alert(`User Debug Info:\nUsername: ${user?.username}\nRole: ${user?.role}\nToken: ${!!token ? 'Present' : 'Missing'}\n\nCheck console for full details.`);
                      }}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      👤 Check User Data
                    </button>
                    <button 
                      onClick={() => {
                        localStorage.clear();
                        alert('Local storage cleared! You will need to log in again.');
                        window.location.href = '/login';
                      }}
                      className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      🗑️ Clear Local Storage
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                <div className="flex items-start space-x-3">
                  <div className="text-yellow-600 text-xl">⚠️</div>
                  <div>
                    <h4 className="font-semibold text-yellow-800">Debug Tools Notice</h4>
                    <p className="text-yellow-700 text-sm mt-1">
                      These tools are for development and testing purposes only. Use with caution on production data.
                      All actions are logged to the browser console for debugging purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;